<template>
    <div>
        <!-- Messenger Button -->
        <messenger-button
            ref="messengerButton"
            :button-icon="buttonIcon"
            :button-image-url="dynamicButtonImageUrl"
            :button-image-alt="buttonImageAlt"
            :button-color="buttonColor"
            :button-text="buttonText"
            :position="position"
            :show-on-mobile="showOnMobile"
            :is-modal-open="isModalOpen"
            :speech-bubble-text="dynamicSpeechBubbleText"
            @click="handleButtonClick"
        />

        <!-- Modal -->
        <div
            v-if="isModalOpen"
            class="floating-modal-backdrop"
            @click="closeModal"
        >
            <div
                class="floating-modal-dialog"
                :class="{
                    'expanded': isExpanded,
                    [`position-${position}`]: true
                }"
                ref="modalDialog"
                @click.stop
            >
                <div class="floating-modal-content">
                    <div class="floating-modal-header">
                        <button
                            type="button"
                            class="floating-modal-close"
                            @click="closeModal"
                            aria-label="Close"
                        >
                            <i class="fas fa-times"></i>
                        </button>
                        <h5 class="floating-modal-title">
                            {{ dynamicModalTitle }}
                        </h5>
                        <button
                            type="button"
                            class="floating-modal-expand d-none d-sm-flex"
                            @click="toggleExpand"
                            :aria-label="isExpanded ? 'Contract' : 'Expand'"
                        >
                            <i class="fas" :class="isExpanded ? 'fa-compress' : 'fa-expand'"></i>
                        </button>
                    </div>
                    <div class="floating-modal-body">
                        <messenger
                            :occasion-id="occasionId"
                            :participant-id="participantId"
                            :ai-enabled="aiEnabled"
                            :faq-list-enabled="faqListEnabled"
                            :show-title="false"
                            :max-questions="maxQuestions"
                            :default-mode="currentMode"
                            :show-metadata="showMetadata"
                            :api-endpoint="apiEndpoint"
                            :is-modal="true"
                            :logo-url="dynamicButtonImageUrl"
                            :logo-alt="buttonImageAlt"
                            :welcome-message="dynamicWelcomeMessage"
                            @question-submitted="$emit('question-submitted', $event)"
                            @response-received="$emit('response-received', $event)"
                            @error-occurred="$emit('error-occurred', $event)"
                            @rate-limit-exceeded="$emit('rate-limit-exceeded', $event)"
                            @contact-submitted="$emit('contact-submitted', $event)"
                            @contact-success="handleContactSuccess"
                            @contact-error="$emit('contact-error', $event)"
                            @mode-changed="handleModeChange"
                        />
                    </div>

                    <!-- Fixed Footer -->
                    <div class="floating-modal-footer">
                        <div class="footer-text">Powered by <a href="https://www.eventrac.co.uk" target="_blank">Eventrac</a> | AI can make mistakes</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import MessengerButton from './MessengerButton.vue'
import Messenger from './Messenger.vue'

export default {
    name: 'MessengerContainer',

    components: {
        MessengerButton,
        Messenger,
    },

    props: {
        // Required props
        occasionId: {
            type: [Number, String],
            required: true
        },
        // Messenger props
        participantId: {
            type: [Number, String],
            default: null
        },
        aiEnabled: {
            type: Boolean,
            default: true
        },
        faqListEnabled: {
            type: Boolean,
            default: true
        },
        defaultMode: {
            type: String,
            default: 'ai',
            validator: value => ['ai', 'faq-list', 'contact'].includes(value)
        },
        showMetadata: {
            type: Boolean,
            default: false
        },
        maxQuestions: {
            type: Number,
            default: 3
        },
        apiEndpoint: {
            type: String,
            default: '/api/v0/ai/ask-question'
        },

        // MessengerButton props
        buttonIcon: {
            type: String,
            default: 'fas fa-question-circle'
        },
        buttonImageUrl: {
            type: String,
            default: 'https://dh97sxltltum5.cloudfront.net/app/ask_evan.png'
        },
        buttonImageAlt: {
            type: String,
            default: 'Chat with us'
        },
        buttonColor: {
            type: String,
            default: 'primary'
        },
        buttonText: {
            type: String,
            default: 'Ask a question'
        },
        position: {
            type: String,
            default: 'bottom-right'
        },
        showOnMobile: {
            type: Boolean,
            default: true
        },

        // Behavior props
        closeOnSuccess: {
            type: Boolean,
            default: true
        },
        widgetId: {
            type: String,
            default: 'messenger-container'
        },

        // Branding props
        speechBubbleText: {
            type: String,
            default: '👋 I’m Evan. I’m here to help if you have any questions.'
        },
        welcomeMessage: {
            type: String,
            default: 'Hi. I\'m Evan, how can I help you today?'
        }
    },

    data() {
        return {
            isModalOpen: false,
            isExpanded: false,
            currentMode: this.defaultMode || 'ai',
            buttonPosition: this.position,
            // Dynamic branding data that can be overridden by API
            dynamicButtonImageUrl: this.buttonImageUrl,
            dynamicWelcomeMessage: this.welcomeMessage,
            dynamicSpeechBubbleText: this.speechBubbleText
        }
    },

    computed: {
        dynamicModalTitle() {
            switch (this.currentMode) {
                case 'ai':
                    return 'Ask us a question'
                case 'faq-list':
                    return 'Read our FAQs'
                case 'contact':
                    return 'Email us'
                default:
                    return 'Ask a question'
            }
        }
    },

    mounted() {
        // Add escape key listener
        document.addEventListener('keydown', this.handleEscapeKey)

        // Register this widget globally so external buttons can trigger it
        if (this.widgetId) {
            window[this.widgetId] = this
        }

        // Load dynamic branding from API if needed
        this.loadBranding()
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.handleEscapeKey)

        // Clean up global reference
        if (this.widgetId && window[this.widgetId] === this) {
            delete window[this.widgetId]
        }
    },

    methods: {
        handleButtonClick(clickData) {
            // Store button position for modal positioning
            this.buttonPosition = (clickData && clickData.position) || this.position
            this.toggleModal()
        },

        toggleModal() {
            if (this.isModalOpen) {
                this.closeModal()
            } else {
                this.openModal()
            }
        },

        openModal(mode = null) {
            // If mode is provided, use it; otherwise determine the best available mode
            if (mode && ['ai', 'faq-list', 'contact'].includes(mode)) {
                this.currentMode = mode
            } else if (!mode) {
                // Determine best mode: AI > FAQ list > Contact
                this.currentMode = this.determineBestMode()
            }

            this.isModalOpen = true
            document.body.style.overflow = 'hidden' // Prevent background scrolling

            // Hide speech bubble when modal opens (from any trigger)
            if (this.$refs.messengerButton) {
                this.$refs.messengerButton.hideSpeechBubble()
                this.$refs.messengerButton.clearTimeouts()
            }

            // Position modal relative to button
            this.$nextTick(() => {
                this.updateModalPosition()
            })

            this.$emit('modal-opened')
        },

        determineBestMode() {
            // Priority: AI if enabled, then FAQ list if enabled, then contact
            if (this.aiEnabled) return 'ai'
            if (this.faqListEnabled) return 'faq-list'
            return 'contact'
        },

        closeModal() {
            this.isModalOpen = false
            document.body.style.overflow = '' // Restore scrolling
            this.$emit('modal-closed')
        },

        handleEscapeKey(event) {
            if (event.key === 'Escape' && this.isModalOpen) {
                this.closeModal()
            }
        },

        toggleExpand() {
            this.isExpanded = !this.isExpanded
            this.$emit(this.isExpanded ? 'modal-expanded' : 'modal-contracted')
        },

        updateModalPosition() {
            if (!this.$refs.modalDialog || !this.$refs.messengerButton) return

            const button = this.$refs.messengerButton.$refs.floatingButton
            if (!button) return

            const buttonRect = button.getBoundingClientRect()
            const buttonBottom = window.innerHeight - buttonRect.bottom

            // Position modal 10px above the button
            const modalBottom = buttonBottom + buttonRect.height + 10

            this.$refs.modalDialog.style.bottom = modalBottom + 'px'

            // Don't override horizontal positioning - let CSS handle it
            // The CSS classes .position-bottom-left and .position-bottom-right handle horizontal positioning
        },

        handleContactSuccess(event) {
            this.$emit('contact-success', event)

            if (this.closeOnSuccess) {
                // Small delay to let user see success message
                setTimeout(() => {
                    this.closeModal()
                }, 1500)
            }
        },

        handleModeChange(newMode) {
            this.currentMode = newMode
            this.$emit('mode-changed', newMode)
        },

        async loadBranding() {
            // Example method for future API integration
            // This could be called on mount or when tenant changes

            try {
                // Example API call (commented out for now)
                // const response = await fetch(`/api/v0/tenant-branding/${this.occasionId}`)
                // const branding = await response.json()

                // if (branding.success && branding.data) {
                //     this.dynamicButtonImageUrl = branding.data.logoUrl || this.buttonImageUrl
                //     this.dynamicWelcomeMessage = branding.data.welcomeMessage || this.welcomeMessage
                //     this.dynamicSpeechBubbleText = branding.data.speechBubbleText || this.speechBubbleText
                // }

                // For now, just use the props (no API call)
                console.log('Branding loaded from props - API integration ready')
            } catch (error) {
                console.warn('Failed to load branding from API, using defaults:', error)
                // Fallback to prop values already set in data()
            }
        }
    }
}
</script>

<style scoped>
/* Custom modal styles */
.floating-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1055;
    padding: 0;
}

.floating-modal-dialog {
    position: fixed;
    right: 20px; /* Default: align with the button's right edge */
    max-width: 400px;
    width: 400px;
    max-height: 70vh;
    height: 600px;
    display: flex;
    flex-direction: column;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Left positioned modal */
.floating-modal-dialog.position-bottom-left,
.floating-modal-dialog.position-top-left {
    right: auto;
    left: 20px; /* Align with the button's left edge */
}

.floating-modal-dialog.expanded {
    /* Keep edge in same position, expand the other direction */
    width: calc(400px + 200px); /* Expand width by 200px */
    max-width: calc(400px + 200px);
    height: calc(600px + 200px); /* Expand height by 200px */
    max-height: calc(600px + 200px);
    z-index: 1060;
}

/* For right-positioned modals, keep right edge in same position */
.floating-modal-dialog.expanded:not(.position-bottom-left):not(.position-top-left) {
    right: 20px; /* Keep original right position */
}

/* For left-positioned modals, keep left edge in same position */
.floating-modal-dialog.expanded.position-bottom-left,
.floating-modal-dialog.expanded.position-top-left {
    left: 20px; /* Keep original left position */
    right: auto;
}

/* Ensure expanded modal doesn't go off-screen on smaller viewports */
@media (max-width: 820px) {
    .floating-modal-dialog.expanded {
        right: 10px;
        width: calc(100vw - 20px);
        max-width: calc(100vw - 20px);
    }
}

/* Ensure expanded modal has enough vertical space */
@media (max-height: 900px) {
    .floating-modal-dialog.expanded {
        height: calc(100vh - 100px);
        max-height: calc(100vh - 100px);
        bottom: 50px;
    }
}

.floating-modal-content {
    background-color: white;
    border-radius: 15px; /* Match speech bubble border radius */
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    height: 100%; /* Fill the full height of the dialog */
    overflow: hidden;
    border: 1px solid #e9ecef; /* Match speech bubble border */
}

.floating-modal-header {
    padding: 0.4rem 0.8rem;
    border-bottom: 1px solid #dee2e6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
}

.floating-modal-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 500;
    color: #495057;
    flex: 1;
    text-align: center;
}

/* On mobile, center the title properly when expand button is hidden */
@media (max-width: 575.98px) {
    .floating-modal-header {
        justify-content: center;
        position: relative;
    }

    .floating-modal-close {
        position: absolute;
        left: 0.8rem;
    }

    .floating-modal-title {
        flex: none;
        text-align: center;
    }
}

.floating-modal-close,
.floating-modal-expand {
    background: transparent;
    border: 0;
    font-size: 1rem;
    line-height: 1;
    color: #6c757d;
    opacity: 0.7;
    cursor: pointer;
    padding: 0.5rem;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;
}

.floating-modal-close:hover,
.floating-modal-expand:hover {
    color: #495057;
    background-color: #f8f9fa;
    opacity: 1;
}

.floating-modal-close:focus,
.floating-modal-expand:focus {
    outline: 2px solid #007bff;
    outline-offset: 1px;
}

.floating-modal-body {
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
    padding-bottom: 0; /* Remove padding since Messenger handles its own spacing */
    position: relative;
}

.floating-modal-footer {
    border-top: 1px solid #e9ecef !important;
    padding: 6px 16px !important;
    background: white !important;
    text-align: center !important;
    flex-shrink: 0; /* Prevent footer from shrinking */
    z-index: 15 !important; /* Higher than Messenger sticky buttons */
    position: relative;
}

.floating-modal-footer .footer-text {
    color: #6c757d !important;
    font-size: 11px !important;
    font-weight: 400 !important;
    letter-spacing: 0.3px !important;
    margin: 0 !important;
    display: block !important;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
    .floating-modal-backdrop {
        padding: 10px;
    }

    .floating-modal-dialog {
        /* Center the modal on mobile instead of positioning by sides */
        right: 10px;
        left: 10px;
        top: 10px;
        bottom: 10px;
        width: auto; /* Override fixed width */
        max-width: none; /* Override max-width */
        height: auto; /* Override fixed height */
        max-height: none; /* Override max-height constraint */
    }

    /* Override left positioning on mobile */
    .floating-modal-dialog.position-bottom-left,
    .floating-modal-dialog.position-top-left {
        left: 10px;
        right: 10px;
    }

    .floating-modal-header {
        padding: 1rem 0.4rem;
    }

    .floating-modal-body {
        padding: 1.5rem;
        padding-bottom: 0;
    }
}

/* Animation */
.floating-modal-backdrop {
    animation: fadeIn 0.15s ease-out;
}

.floating-modal-content {
    animation: slideIn 0.15s ease-out;
}

.floating-modal-dialog.position-bottom-left .floating-modal-content,
.floating-modal-dialog.position-top-left .floating-modal-content {
    animation: slideInLeft 0.15s ease-out;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: scale(0.8) translateY(20px);
        transform-origin: bottom right;
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
        transform-origin: bottom right;
    }
}

@keyframes slideInLeft {
    from {
        opacity: 0;
        transform: scale(0.8) translateY(20px);
        transform-origin: bottom left;
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
        transform-origin: bottom left;
    }
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/Messenger/MessengerContainer.vue